import React, { useEffect, useState } from "react";
import "../styles/PP_NegotiatorDetailModal.css";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { fetchGoalsFromSheet } from "../utils/fetchGoalsFromSheet";
import { saveGoalToSheet } from "../utils/saveGoalToSheet";

ChartJS.register(ArcElement, Tooltip, Legend);

const NegotiatorDetailModal = ({ negotiator, onClose, onSaveSuccess, appliedStartDate = null }) => {
  const [goal, setGoal] = useState(negotiator.goal || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [hidePastGoalColumns, setHidePastGoalColumns] = useState(false);

  // Check date for goal column visibility
  useEffect(() => {
    const cutoffDate = new Date('2025-09-01');
    const appliedStart = appliedStartDate ? new Date(appliedStartDate) : null;
    
    // Hide goal columns if start date is on or before September 2025
    setHidePastGoalColumns(appliedStart && appliedStart <= cutoffDate);
  }, [appliedStartDate]);

  useEffect(() => {
    async function loadGoal() {
      try {
        const goals = await fetchGoalsFromSheet();
        const name = negotiator.AssignedNegotiator?.trim();
        if (name && goals[name] !== undefined) {
          setGoal(goals[name]);
        }
      } catch (error) {
        console.error("Failed to load goals:", error);
      }
    }
    loadGoal();
  }, [negotiator]);

  const production = negotiator.ProductionToDateOTB || 0;
  const remaining = hidePastGoalColumns ? 0 : Math.max(goal - production, 0);
  const isBehind = !hidePastGoalColumns && production < goal;
  const achievementPercent = hidePastGoalColumns 
    ? "0.00" 
    : (goal > 0 ? ((production / goal) * 100).toFixed(2) : "0.00");

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await saveGoalToSheet(negotiator.AssignedNegotiator, goal);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      alert("❌ Failed to save goal.");
    } finally {
      setIsSaving(false);
    }
  };

  const doughnutData = {
    labels: ["Achieved", "Remaining"],
    datasets: [
      {
        data: [production, remaining],
        backgroundColor: ["#2ecc71", "#e74c3c"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="close-btn">
          ×
        </button>
        <h3>{negotiator.AssignedNegotiator} - Detail View</h3>

        {/* Status - Only show if not hiding past goal columns */}
        {!hidePastGoalColumns && (
          <div className={`status ${isBehind ? "behind" : "on-track"}`}>
            <span>
              <strong>Status:</strong>
            </span>{" "}
            {isBehind ? "❌ Behind Goal" : "✅ On Track"}
          </div>
        )}

        {/* Doughnut Chart */}
        <div className="chart-container">
          <Doughnut data={doughnutData} />
        </div>

        {/* Detail Info */}
        <ul className="details-list">
          {!hidePastGoalColumns && (
            <>
              <li>
                <strong>Goal Achievement:</strong> {achievementPercent}%
              </li>
              <li>
                <strong>Production To Date:</strong> $
                {production?.toLocaleString()}
              </li>

              {/* Editable Goal */}
              <li>
                <strong>Goal:</strong>{" "}
                <input
                  type="number"
                  value={goal}
                  onChange={(e) => setGoal(Number(e.target.value))}
                  style={{
                    width: "120px",
                    marginLeft: "5px",
                    padding: "4px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                  }}
                />
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{
                    marginLeft: "10px",
                    padding: "4px 10px",
                    background: "#2ecc71",
                    border: "none",
                    borderRadius: "5px",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </li>
            </>
          )}

          <li>
            <strong>Underway:</strong> $
            {negotiator.TotalProductionUnderway?.toLocaleString()}
          </li>
          <li>
            <strong>Biggest Fee Pending:</strong> $
            {negotiator.BiggestFeePending?.toLocaleString()}
          </li>
          <li>
            <strong>Individual Fee:</strong> $
            {negotiator.IndividualFee?.toLocaleString()}
          </li>
          <li>
            <strong>SIF Count:</strong> {negotiator.IndividualSIFCount}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default NegotiatorDetailModal;